import { Fragment, useMemo } from "react"
import { Link } from "@tanstack/react-router"

import type { TagsGet, ThreadsGet } from "@productlane/api"
import { cn } from "@productlane/lib/src/browser"
import { formatRevenue } from "@productlane/lib/src/format-revenue"
import { Empty, Tooltip } from "@productlane/ui"

import type { ReportingQueryProps } from "@/screens/app/reporting/reporting-query"
import EmptyTags from "@/assets/empty-states/empty-tags.png"
import { ReportingSectionHeader } from "@/components/reporting-section-header"
import { Tag } from "@/components/tag"
import { TagFeedbackChart } from "@/components/tag-feedback-chart"
import { api } from "@/lib/api"
import { useZeroWorkspace } from "@/lib/zero"

interface TagsVisualizationProps extends ReportingQueryProps {
	showActions?: boolean
}

export const TagsVisualization = ({
	showActions: _showActions = false,
	startDate,
	endDate,
	...filters
}: TagsVisualizationProps) => {
	const workspace = useZeroWorkspace()

	const tagsMetricsQuery = api.workspace.getTagsMetrics.useQuery(
		{ startDate, endDate, ...filters },
		{
			cacheTime: Infinity,
			staleTime: Infinity,
		},
	)

	const { maxMap, filteredTags, threads, tagGroups, hasVisibleTags } =
		useMemo(() => {
			const tagGroups = tagsMetricsQuery.data?.tagGroups || []
			const map = new Map<string, number>()
			const transformedTags: Array<{
				id: string
				name: string
				color: string
				icon: string
				tagGroupId: string
				feedbackIds: string[]
				importance: {
					painLevel: number
				}
			}> = []

			const transformedThreads: Array<{
				id: string
				createdAtIso: string
			}> = []

			tagGroups.forEach((tagGroup) => {
				const maxPainLevel = Math.max(
					...tagGroup.tags.map((tag) => tag.painLevel),
					0,
				)
				map.set(tagGroup.id, maxPainLevel)

				tagGroup.tags.forEach((tag) => {
					transformedTags.push({
						id: tag.id,
						name: tag.name,
						color: tagGroup.color,
						icon: tag.icon,
						tagGroupId: tagGroup.id,
						feedbackIds: tag.feedbackDates.map(
							(_, idx) => `feedback-${tag.id}-${idx}`,
						),
						importance: {
							painLevel: tag.painLevel,
						},
					})

					tag.feedbackDates.forEach((dateStr, idx) => {
						transformedThreads.push({
							id: `feedback-${tag.id}-${idx}`,
							createdAtIso: dateStr,
						})
					})
				})
			})

			const tagsWithFeedback = transformedTags.filter(
				(t) => t.feedbackIds.length >= 1,
			)

			return {
				maxMap: map,
				filteredTags: transformedTags,
				threads: transformedThreads,
				tagGroups,
				hasVisibleTags: tagsWithFeedback.length > 0,
			}
		}, [tagsMetricsQuery.data])

	const tagMetricsMap = useMemo(() => {
		const map = new Map<string, number>()
		tagsMetricsQuery.data?.tagGroups.forEach((tagGroup) => {
			tagGroup.tags.forEach((tag) => {
				map.set(tag.id, tag.aggregatedRevenue)
			})
		})
		return map
	}, [tagsMetricsQuery.data])

	return (
		<>
			{hasVisibleTags || tagsMetricsQuery.isLoading ? (
				<div>
					{tagGroups.map((tagGroup) => {
						const groupTags = filteredTags
							.filter((t) => t.tagGroupId === tagGroup.id)
							.filter((t) => t.feedbackIds.length >= 1)
							.sort((a, b) => b.feedbackIds.length - a.feedbackIds.length)

						if (groupTags.length === 0) return null

						const totalPainLevel = groupTags.reduce(
							(sum, tag) => sum + tag.importance.painLevel,
							0,
						)

						return (
							<Fragment key={tagGroup.id}>
								<ReportingSectionHeader
									title={tagGroup.name}
									count={totalPainLevel}
								/>
								<div>
									{groupTags.map((tag) => {
										let relativePainLevel = 0
										const max = maxMap.get(tag.tagGroupId)
										if (max) {
											relativePainLevel = tag.importance.painLevel / max
										}
										const painLevelInPx = Math.round(relativePainLevel * 40)

										return (
											<Link
												key={tag.id}
												className={cn(
													"flex items-center gap-4 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-850",
												)}
												to="/tags/$id"
												params={{ id: tag.id }}
											>
												<Tag
													tag={tag}
													groupColor={tagGroup.color}
													variant="transparent"
													className="min-w-[6rem] flex-1 gap-4 truncate !border-none"
												/>

												<div className="w-16 flex-none md:w-32">
													<TagFeedbackChart
														tag={tag as unknown as TagsGet[number]}
														threads={threads as unknown as ThreadsGet}
														color={tagGroup.color}
													/>
												</div>

												<div className="ml-2 flex w-auto flex-none items-center gap-2 text-gray-700 md:ml-6 md:w-20 dark:text-gray-300">
													<div className="relative hidden h-1 w-10 rounded bg-gray-200 md:block dark:bg-gray-800">
														<div
															className="absolute h-1 rounded"
															style={{
																width: `${painLevelInPx}px`,
																backgroundColor: tagGroup.color,
															}}
														/>
													</div>

													<p className="w-7">{tag.importance.painLevel}</p>
												</div>

												{workspace && (
													<Tooltip label="Aggregated Revenue">
														<div className="ml-2 flex w-10 flex-none items-center text-gray-700 dark:text-gray-300">
															<p className="text-sm">
																{formatRevenue({
																	revenue: tagMetricsMap.get(tag.id) ?? 0,
																	revenueFormatting:
																		workspace.revenueFormatting,
																	currency: workspace.revenueCurrency,
																})}
															</p>
														</div>
													</Tooltip>
												)}
											</Link>
										)
									})}
								</div>
							</Fragment>
						)
					})}
				</div>
			) : (
				<div className="flex justify-center py-12">
					<Empty
						title="Tags"
						text="Identify trending topics and themes in customer conversations."
						image={
							<img width={130} height={128} src={EmptyTags} alt="Empty tags" />
						}
					/>
				</div>
			)}
		</>
	)
}
